// @flow

import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { getPassiveIfSupported } from '../../utils/domUtils';

// [MC] SimpleSticky is the least simple sticky component ever.
// we should clean this up!

type Props = {
  classes: Object,
  className?: string,
  children?: any,
  isStuckFunc?: (isSticky: boolean) => boolean,
  offsetTop?: number,
};

type State = {
  isStuck: boolean,
  width: number,
  height: number,
  containerOffset: number,
};

function featureTest(property, value) {
  // Thanks Modernizr!
  const prop = `${property}:`;
  const element = document.createElement('test');
  const style = element.style;

  style.cssText = `${prop + ['-webkit-', '-moz-', '-ms-', '-o-', ''].join(`${value};${prop}`) + value};`;
  return style[property];
}

const supportsPositionSticky = featureTest('position', 'sticky');

let ScrollHelper;
if (!supportsPositionSticky) {
  // set up a helper that'll ensure sure we only need 1 listener on the scroll event
  const listeners = new Set();
  ScrollHelper = {
    onScroll: (e: React.SyntheticEvent) => {
      window.requestAnimationFrame(() => {
        listeners.forEach(callback => callback(e));
      });
    },
    addScrollListener: (callback) => {
      listeners.add(callback);
    },
    removeScrollListener: (callback) => {
      listeners.delete(callback);
    },
  };
  window.addEventListener('scroll', ScrollHelper.onScroll, getPassiveIfSupported());
}

class SimpleSticky extends React.Component {
  props: Props;

  state: State = {
    isStuck: false,
    width: 0,
    height: 0,
    containerOffset: 0,
  };

  scrollContainer;

  // eslint-disable-line react/sort-comp
  container;

  stickyContainer;

  sticky;

  static defaultProps = {
    className: undefined,
    isStuckFunc: undefined,
    children: undefined,
    offsetTop: 0,
  };

  componentDidMount() {
    if (!supportsPositionSticky) {
      this.scrollContainer = document.scrollingElement || document.documentElement;
      this.container = this.stickyContainer.parentElement;
      ScrollHelper.addScrollListener(this.onItemsScroll);
    }
  }

  componentWillUnmount() {
    if (!supportsPositionSticky) {
      ScrollHelper.removeScrollListener(this.onItemsScroll);
    }
  }

  /** Scroll listener that makes the sticky part sit in the correct spot */
  onItemsScroll = (e) => {
    if ((e.target.scrollingElement || e.target.documentElement) === this.scrollContainer) {
      const { offsetTop } = this.props;
      const stickyElement = this.sticky;
      // get the props of our element as if it wasn't stuck. This gets the accurate
      // current top/height/width
      const oldPosition = stickyElement.style.position;
      stickyElement.style.position = 'static';
      const { top, height, width } = stickyElement.getBoundingClientRect();
      stickyElement.style.position = oldPosition;

      // find out if the element in question is no longer in view, thus it should be stuck
      const isStuck = top <= offsetTop;
      let containerOffset = 0;
      if (this.container && isStuck) {
        // stick to our container like position: sticky, so find the bottom of it
        // and compensate the offset by the amount we'd overshoot it by
        const containerRect = this.container.getBoundingClientRect();
        containerOffset = Math.max(height - containerRect.bottom, 0);
      }
      // only update if something has changed
      if (this.state.isStuck !== isStuck
           || this.state.width !== width
          || this.state.containerOffset !== containerOffset
          || this.state.height !== height) {
        this.setState({ isStuck, width, containerOffset, height });
      }
    }
  }

  setStickyRef = (node) => {
    this.sticky = node;
  }

  setStickyContainerRef = (node) => {
    this.stickyContainer = node;
  }

  render() {
    const {
      classes,
      children,
      isStuckFunc,
      className,
      offsetTop,
    } = this.props;
    const {
      isStuck,
      width,
      containerOffset,
      height,
    } = this.state;

    const style = {};
    if (supportsPositionSticky) {
      const isReallyStuck = isStuckFunc ? isStuckFunc(true) : true;
      if (isReallyStuck) {
        style.top = offsetTop;
      }
      return (
        <div
          className={classNames(className, { [classes.itsActuallySticky]: isReallyStuck })}
          ref={this.setStickyRef}
          style={style}
        >
          {children}
        </div>
      );
    }

    // if we don't support position: sticky then we -always- use our
    // 'isStuck' to see if we're stuck as the implementation (fixed) makes
    // for incorrect positioning if we allow the user to return true on isStuckFunc
    // when it's still scrolled in view
    const isReallyStuck = isStuckFunc ? isStuckFunc(isStuck) && isStuck : isStuck;
    if (isReallyStuck) {
      style.width = width;
      style.top = offsetTop - containerOffset;
      style.height = height;
    }

    return (
      <div
        className={className}
        style={isReallyStuck ? style : undefined}
        ref={this.setStickyContainerRef}
      >
        <div
          className={classNames(className, { [classes.stuck]: isReallyStuck })}
          style={style}
          ref={this.setStickyRef}
        >
          {children}
        </div>
      </div>
    );
  }
}

function applyStyles(theme) {
  return {
    itsActuallySticky: {
      position: 'sticky',
      zIndex: theme.zIndex.sticky,
    },
    stuck: {
      position: 'fixed',
      zIndex: theme.zIndex.sticky,
    },
  };
}

export default useSheet(applyStyles)(SimpleSticky);
